import React, { useEffect, useState } from "react";
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Link,
  BottomNavigation,
  BottomNavigationAction,
  Paper,
  Stack,
  useMediaQuery,
} from "@mui/material";
import FlashOnRoundedIcon from "@mui/icons-material/FlashOnRounded";
import TrackChangesRoundedIcon from "@mui/icons-material/TrackChangesRounded";
import DynamicFeedRoundedIcon from "@mui/icons-material/DynamicFeedRounded";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MapOutlinedIcon from "@mui/icons-material/MapOutlined";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import { useLocation, useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import TopBar from "../components/common/TopBar";
import AppSidebar from "../components/common/AppSidebar";
import InviteDialog from "../components/common/InviteDialog";
import PagePurposeHelp from "../components/common/PagePurposeHelp";
import { apiFetch } from "../api/apiFetch";
import { fetchAccountSettings } from "../api/user";

const MOBILE_NAV_ITEMS = [
  { value: "today", label: "Today", path: "/", icon: <FlashOnRoundedIcon /> },
  { value: "sprint", label: "Account", path: "/accountability", icon: <TrackChangesRoundedIcon /> },
  { value: "network", label: "Activity", path: "/activity", icon: <DynamicFeedRoundedIcon /> },
  { value: "connections", label: "People", path: "/connections", icon: <PeopleAltOutlinedIcon /> },
  { value: "plan", label: "Plan", path: "/plan", icon: <MapOutlinedIcon /> },
  { value: "intelligence", label: "Insights", path: "/intelligence", icon: <AutoAwesomeRoundedIcon /> },
];

function getMobileNavValue(pathname) {
  if (pathname === "/" || pathname.startsWith("/today")) return "today";
  if (pathname.startsWith("/accountability") || pathname.startsWith("/sprint") || pathname.startsWith("/challenge") || pathname.startsWith("/trustbud")) return "sprint";
  if (pathname.startsWith("/activity") || pathname.startsWith("/updates") || pathname.startsWith("/moments")) return "network";
  if (pathname.startsWith("/network") || pathname.startsWith("/connections") || pathname.startsWith("/companions")) return "connections";
  if (pathname.startsWith("/plan") || pathname.startsWith("/manage-vision")) return "plan";
  if (pathname.startsWith("/intelligence")) return "intelligence";
  return "";
}

function AppLayout({ children, onLogout, username }) {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("lg"));
  const showMobileBottomNav = useMediaQuery(theme.breakpoints.down("lg"));
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem("trustbud:sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [termsGateOpen, setTermsGateOpen] = useState(false);
  const [termsSubmitting, setTermsSubmitting] = useState(false);
  const [termsError, setTermsError] = useState("");
  const [profileImage, setProfileImage] = useState("");
  const [isStaff, setIsStaff] = useState(false);
  const [hasUnreadActivity, setHasUnreadActivity] = useState(false);
  const [hasIncomingRequests, setHasIncomingRequests] = useState(false);

  useEffect(() => {
    function handleOpenInvite() {
      setInviteOpen(true);
    }
    window.addEventListener("trustbud:open-invite", handleOpenInvite);
    return () => window.removeEventListener("trustbud:open-invite", handleOpenInvite);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    function handleActivityUnread(event) {
      setHasUnreadActivity(Boolean(event.detail?.hasUnread));
    }
    function handleRequestsUnread(event) {
      setHasIncomingRequests(Boolean(event.detail?.count));
    }
    window.addEventListener("trustbud:timeline-unread", handleActivityUnread);
    window.addEventListener("trustbud:requests-unread", handleRequestsUnread);
    return () => {
      window.removeEventListener("trustbud:timeline-unread", handleActivityUnread);
      window.removeEventListener("trustbud:requests-unread", handleRequestsUnread);
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    async function checkTerms() {
      try {
        const data = await apiFetch("/account-settings/");
        if (!isMounted) return;
        if (data?.terms_response !== "accepted") {
          setTermsGateOpen(true);
        }
      } catch {
        // If we cannot verify, keep app usable for now.
      }
    }
    checkTerms();
    return () => {
      isMounted = false;
    };
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem("trustbud:sidebar-collapsed", String(sidebarCollapsed));
    } catch {
      // ignore storage errors
    }
  }, [sidebarCollapsed]);

  useEffect(() => {
    let isMounted = true;
    async function loadAccount() {
      try {
        const data = await fetchAccountSettings();
        if (!isMounted) return;
        setProfileImage(data?.profile_image || "");
        setIsStaff(Boolean(data?.is_staff));
      } catch {
        if (isMounted) {
          setProfileImage("");
          setIsStaff(false);
        }
      }
    }
    loadAccount();
    return () => {
      isMounted = false;
    };
  }, []);

  async function handleTermsResponse(responseValue) {
    setTermsSubmitting(true);
    setTermsError("");
    try {
      await apiFetch("/account-settings/", {
        method: "PATCH",
        body: JSON.stringify({ terms_response: responseValue }),
      });
      if (responseValue === "accepted") {
        setTermsGateOpen(false);
      } else {
        setTermsGateOpen(false);
        onLogout?.();
      }
    } catch (err) {
      setTermsError(err?.message || "Unable to save your response.");
    } finally {
      setTermsSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#f3f6fa",
      }}
    >
      {isDesktop ? (
        <Box sx={{ display: "flex", alignItems: "stretch", minHeight: "100vh" }}>
          <AppSidebar
            onLogout={onLogout}
            username={username}
            profileImage={profileImage}
            isStaff={isStaff}
            collapsed={sidebarCollapsed}
            onToggleCollapse={() => setSidebarCollapsed((prev) => !prev)}
          />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                px: { lg: 2.2, xl: 3 },
                pt: { lg: 1.8, xl: 2.1 },
                pb: 6,
              }}
            >
              <Box key={location.pathname} className="route-fade">
                {children}
              </Box>
            </Box>
          </Box>
        </Box>
      ) : (
        <>
          <TopBar onLogout={onLogout} username={username} />
          <Box sx={{ px: { xs: 1.25, sm: 2.5 }, mt: { xs: 2, sm: 3 }, pb: { xs: 11, sm: 12 } }}>
            <Box key={location.pathname} className="route-fade">
              {children}
            </Box>
          </Box>
          {showMobileBottomNav ? (
            <Paper
              elevation={0}
              sx={{
                position: "fixed",
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 1200,
                borderTop: "1px solid rgba(15, 23, 42, 0.08)",
                backgroundColor: "rgba(255,255,255,0.96)",
                backdropFilter: "blur(18px)",
                pb: "env(safe-area-inset-bottom)",
              }}
            >
              <BottomNavigation
                showLabels
                value={getMobileNavValue(location.pathname)}
                onChange={(_, nextValue) => {
                  const item = MOBILE_NAV_ITEMS.find((navItem) => navItem.value === nextValue);
                  if (item) navigate(item.path);
                }}
                sx={{
                  height: 58,
                  "& .MuiBottomNavigationAction-root": {
                    minWidth: 0,
                    px: 0.35,
                    color: "#60738c",
                  },
                  "& .Mui-selected": {
                    color: "#17345d",
                  },
                  "& .MuiBottomNavigationAction-label": {
                    fontSize: "0.64rem",
                    fontWeight: 800,
                  },
                  "& .MuiSvgIcon-root": {
                    fontSize: 22,
                  },
                }}
              >
                {MOBILE_NAV_ITEMS.map((item) => (
                  <BottomNavigationAction
                    key={item.value}
                    value={item.value}
                    label={item.label}
                    icon={
                      <Box sx={{ position: "relative", display: "inline-flex" }}>
                        {item.icon}
                        {((item.value === "network" && hasUnreadActivity) ||
                          (item.value === "connections" && hasIncomingRequests)) && (
                          <Box
                            sx={{
                              position: "absolute",
                              top: -3,
                              right: -5,
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "#dc2626",
                              border: "1px solid white",
                            }}
                          />
                        )}
                      </Box>
                    }
                  />
                ))}
              </BottomNavigation>
            </Paper>
          ) : null}
        </>
      )}
      <PagePurposeHelp />
      <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} />
      <Dialog
        open={termsGateOpen}
        onClose={() => {}}
        disableEscapeKeyDown
      >
        <DialogTitle>Updated Terms & Community Standards</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2">
              TrustBud is an accountability relationship product. We do not tolerate harassment, hate, threats, sexual content,
              scams, or abusive behavior.
            </Typography>
            <Typography variant="body2">
              Please review and respond to continue. You can read the full terms here:{" "}
              <Link href="/terms" target="_blank" rel="noreferrer">
                TrustBud Terms & Community Standards
              </Link>
              .
            </Typography>
            {termsError ? (
              <Typography color="error" variant="body2">
                {termsError}
              </Typography>
            ) : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            onClick={() => handleTermsResponse("declined")}
            disabled={termsSubmitting}
          >
            Decline
          </Button>
          <Button
            variant="contained"
            onClick={() => handleTermsResponse("accepted")}
            disabled={termsSubmitting}
          >
            Accept & Continue
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default AppLayout;
